import { Text } from '@chakra-ui/react'
import { Format, ScalarContentNode } from '@helo/cms'
import { merge } from 'lodash'

import { InlineSkeleton } from '../../components/SkeletonLoader'
import { ConditionalTooltip } from '../../components/Tooltip/Tooltip'
import { getMeaningByGoalDirection } from '../goalDirection'
import { getSuffixSizeStyles } from '../utils'
import { NumberDataType } from './number.types'

export interface NumberRendererProps {
  contentNode: ScalarContentNode
  isLoading?: boolean
}

export const NumberRenderer = ({
  contentNode,
  isLoading,
  ...props
}: NumberRendererProps) => {
  const showSkeleton = !!isLoading

  const { goalCondition, size, _tooltip } = contentNode.state ?? {}
  const styles = contentNode.state?.styles
  const value = contentNode.data as NumberDataType['value']

  const parts = Format.numberParts(contentNode.state ?? {})(value)

  const valueMeaning = getMeaningByGoalDirection(goalCondition, value)

  const suffixSizeStyles = getSuffixSizeStyles(size)

  // Extract styles object if it exists and is an object
  const customStyles =
    styles && typeof styles === 'object'
      ? { ...styles }
      : ({} as Record<string, unknown>)

  const hasExplicitColor =
    customStyles.color != null && customStyles.color !== ''

  // Text theme baseStyle sets _dark.color, which beats a plain `color` prop in dark mode.
  // Mirror explicit colors into _dark so CMS `state.styles` wins.
  const valueStyles = hasExplicitColor
    ? {
        ...customStyles,
        _dark: {
          ...(typeof customStyles._dark === 'object' &&
          customStyles._dark !== null
            ? customStyles._dark
            : {}),
          color: customStyles.color,
        },
      }
    : customStyles

  const light = {
    color:
      valueMeaning === 'good'
        ? 'goalColors.goodTurquoise'
        : 'goalColors.badPink',
  }

  const _dark = {
    color:
      valueMeaning === 'good'
        ? 'goalColors._dark.goodTurquoise'
        : 'goalColors._dark.badRed',
  }

  const useGoalColors = Boolean(valueMeaning) && !hasExplicitColor

  // Text `size` (e.g. medium) sets lineHeight 16px; CMS `styles.fontSize` alone
  // does not clear it, so large numbers look vertically clipped / cramped.
  const valueStylesWithLineHeight =
    valueStyles.fontSize != null && valueStyles.lineHeight == null
      ? { ...valueStyles, lineHeight: 'normal' }
      : valueStyles

  const affixStyles = {
    ...suffixSizeStyles,
    _dark: valueMeaning ? _dark : {},
  }

  return (
    <ConditionalTooltip isDisabled={showSkeleton} tooltipOptions={_tooltip}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '6px',
          flexWrap: 'wrap',
        }}
        {...props}
      >
        {!showSkeleton && parts.prefix && (
          <Text {...affixStyles}>{parts.prefix}</Text>
        )}
        <Text
          size={size}
          {...(useGoalColors ? light : {})}
          _dark={useGoalColors ? _dark : undefined}
          {...valueStylesWithLineHeight}
        >
          <InlineSkeleton isLoading={showSkeleton}>
            {parts.value}
          </InlineSkeleton>
        </Text>
        {!showSkeleton && parts.suffix && (
          <Text {...affixStyles}>{parts.suffix}</Text>
        )}
      </div>
    </ConditionalTooltip>
  )
}
